/** Per-task-type approval actions for tasks paused in AWAITING_APPROVAL.
 *
 *  When a task type has an entry here, TaskDetailsDialog renders it INSTEAD
 *  of the generic "Approve next step" button — a bare approval without a
 *  payload is ambiguous for tasks whose approval carries a selection (the
 *  AI import's per-record checkboxes). Cancel stays available either way.
 */
import RateReviewIcon from '@mui/icons-material/RateReview';
import { Button } from '@mui/material';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { TASK_STATE, type BackgroundTaskDetail } from '@/api/bgtasks';
import { PermissionGate } from '@/components/PermissionGate';
import { AiImportResultsPanel } from '@/features/ai-import/AiImportResultsPanel';
import { AuxSyncResultPanel } from '@/features/aux-sync/AuxSyncResultPanel';
import { CompositionResultPanel } from '@/features/design-imports/CompositionResultPanel';
import { BookingEditResultPanel } from '@/features/bookings/edit/BookingEditResultPanel';
import { EmitEventsResultPanel } from '@/features/emit-events/EmitEventsResultPanel';
import { EntityExportResultPanel } from '@/features/exports/EntityExportResultPanel';
import { SyncProductsResultPanel } from '@/features/sync/SyncProductsResultPanel';

export const APPROVAL_ACTIONS: Record<string, (task: BackgroundTaskDetail) => ReactNode> = {
  ai_import: (task) => (
    <PermissionGate perm="console_bg_ai_import">
      <Button
        variant="contained"
        color="primary"
        component={RouterLink}
        to={`/ai-import/${task.id}/review`}
        startIcon={<RateReviewIcon />}
      >
        Review drafts
      </Button>
    </PermissionGate>
  ),
  sync_aux_info: (task) => (
    <PermissionGate perm="console_bg_aux_sync">
      <Button
        variant="contained"
        color="primary"
        component={RouterLink}
        to={`/sync/aux-info/${task.id}/review`}
        startIcon={<RateReviewIcon />}
      >
        Review records
      </Button>
    </PermissionGate>
  ),
};

/** Post-completion panels per task type (rendered in the summary tab). */
export const RESULT_PANELS: Record<string, (task: BackgroundTaskDetail) => ReactNode> = {
  ai_import: (task) => <AiImportResultsPanel taskId={task.id} />,
  sync_aux_info: (task) => (
    <AuxSyncResultPanel taskId={task.id} tenantId={task.tour_operator?.id ?? null} />
  ),
  entity_export: (task) => <EntityExportResultPanel taskId={task.id} />,
  composition_import: (task) => <CompositionResultPanel taskId={task.id} />,
  console_booking_edit: (task) => <BookingEditResultPanel taskId={task.id} />,
  console_booking_emit_events: (task) => <EmitEventsResultPanel taskId={task.id} />,
  console_order_emit_events: (task) => <EmitEventsResultPanel taskId={task.id} />,
  // What a product sync did with combined, split-off and left-out products, and the
  // booking-move tasks it launched (plan 2026-10-11 product-sync joins §4, §5, §5a); a
  // dry run shows the plan.
  sync_products: (task) => <SyncProductsResultPanel taskId={task.id} />,
  sync_products_with_approval: (task) => <SyncProductsResultPanel taskId={task.id} />,
  fetch_products: (task) => <SyncProductsResultPanel taskId={task.id} dryRun />,
};

/** Persistent per-task-type links in the summary tab, independent of state.
 *  For ai_import: back to the drafts review once drafts are staged (the
 *  artifact survives completion; cancel-from-awaiting deletes it, so the
 *  link disappears exactly when the page would have nothing to show).
 *  Hidden while AWAITING_APPROVAL — the primary "Review drafts" button
 *  already leads there. */
export const DETAIL_LINKS: Record<string, (task: BackgroundTaskDetail) => ReactNode> = {
  sync_aux_info: (task) => {
    const hasRecords = (task.artifacts ?? []).some((a) => a.name === 'aux_records.json');
    if (!hasRecords || task.state === TASK_STATE.AWAITING_APPROVAL) return null;
    return (
      <PermissionGate perm="console_bg_aux_sync">
        <Button
          size="small"
          variant="outlined"
          component={RouterLink}
          to={`/sync/aux-info/${task.id}/review`}
          startIcon={<RateReviewIcon />}
        >
          View extracted records
        </Button>
      </PermissionGate>
    );
  },
  ai_import: (task) => {
    const hasDrafts = (task.artifacts ?? []).some((a) => a.name === 'drafts.json');
    if (!hasDrafts || task.state === TASK_STATE.AWAITING_APPROVAL) return null;
    return (
      <PermissionGate perm="console_bg_ai_import">
        <Button
          size="small"
          variant="outlined"
          component={RouterLink}
          to={`/ai-import/${task.id}/review`}
          startIcon={<RateReviewIcon />}
        >
          View extracted drafts
        </Button>
      </PermissionGate>
    );
  },
};
